import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ModalNumberInput.css";

function clampNumber(value, min, max) {
	let out = value;
	if (typeof min === "number") out = Math.max(min, out);
	if (typeof max === "number") out = Math.min(max, out);
	return out;
}

function parseDraftToNumber(draft, fallback) {
	if (draft === "" || draft === null || draft === undefined) return fallback;
	const parsed = Number(draft);
	if (!Number.isFinite(parsed)) return fallback;
	return parsed;
}

export default function ModalNumberInput({
	value,
	onApply,
	label,
	title,
	min,
	max,
	step = 1,
	disabled = false,
	formatDisplay,
	style,
}) {
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState(String(value ?? 0));
	const inputRef = useRef(null);

	const displayValue = useMemo(() => {
		if (typeof formatDisplay === "function") return formatDisplay(value);
		return String(value ?? "");
	}, [formatDisplay, value]);

	useEffect(() => {
		if (!open) {
			setDraft(String(value ?? 0));
		}
	}, [open, value]);

	useEffect(() => {
		if (!open) return;
		const t = setTimeout(() => {
			if (inputRef.current) {
				inputRef.current.focus();
				inputRef.current.select?.();
			}
		}, 0);
		return () => clearTimeout(t);
	}, [open]);

	useEffect(() => {
		if (!open) return;
		const onKeyDown = (e) => {
			if (e.key === "Escape") {
				e.preventDefault();
				setOpen(false);
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [open]);

	const apply = (shouldClose) => {
		const fallback = Number.isFinite(Number(value)) ? Number(value) : 0;
		const parsed = parseDraftToNumber(draft, fallback);
		const next = clampNumber(parsed, min, max);
		onApply?.(next);
		if (shouldClose) setOpen(false);
	};

	const nudgeDraft = (direction) => {
		const fallback = Number.isFinite(Number(value)) ? Number(value) : 0;
		const parsed = parseDraftToNumber(draft, fallback);
		const stepNumber = Number.isFinite(Number(step)) ? Number(step) : 1;
		const next = clampNumber(parsed + direction * stepNumber, min, max);
		setDraft(String(next));
	};

	const cancel = () => {
		setDraft(String(value ?? 0));
		setOpen(false);
	};

	return (
		<>
			<button
				type="button"
				className="mni-display"
				disabled={disabled}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					setOpen(true);
				}}
				style={style}
				aria-label={label || title || "Edit value"}
			>
				{displayValue}
			</button>


			{open &&
				typeof document !== "undefined" &&
				createPortal(
					<div className="mni-overlay" role="dialog" aria-modal="true" onMouseDown={cancel}>
						<div
							className="mni-modal"
							onMouseDown={(e) => {
								e.stopPropagation();
							}}
						>
							<div className="mni-header">
								<div className="mni-title">{title || label || "Edit"}</div>
								<button
									type="button"
									className="mni-close"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										cancel();
									}}
									aria-label="Close"
								>
									×
								</button>
							</div>
							{label && <div className="mni-label">{label}</div>}
							<div className="mni-inputRow">
								<button
									type="button"
									className="mni-step"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										nudgeDraft(-1);
									}}
									aria-label="Decrement"
								>
									−
								</button>
								<input
									ref={inputRef}
									type="number"
									inputMode="numeric"
									min={min}
									max={max}
									step={step}
									value={draft}
									onChange={(e) => setDraft(e.target.value)}
									className="mni-input"
								/>
								<button
									type="button"
									className="mni-step"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										nudgeDraft(1);
									}}
									aria-label="Increment"
								>
									+
								</button>
							</div>
							<div className="mni-actions">
								<button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										apply(false);
									}}
									className="mni-btn"
								>
									Apply
								</button>
								<button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										apply(true);
									}}
									className="mni-btn mni-btn-primary"
								>
									OK
								</button>
								<button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										cancel();
									}}
									className="mni-btn"
								>
									Cancel
								</button>
							</div>
						</div>
					</div>,
					document.body
				)}
		</>
	);
}
